/* eslint-disable @reatom/unit-naming-rule */
import { useAction, useAtom } from '@reatom/npm-react'
import { memo, useEffect, useMemo } from 'react'
import { usePreviousDistinct } from 'react-use'

import { BigNumber, Equivalence, NumericFormat, Ordering } from '@blade/std-lib'
import { tradePriceType } from '@root/features/trade/trading'
import { formatMarketCapShort } from '@root/shared/core/format'

import { reatomPairMetadataModel } from '../atoms/reatom-pair-metadata-model'

import type { PairModel } from '@root/entities/pair'

type PairMetadataProps = {
  pair: PairModel
}
const eqBn = Equivalence.nullable(BigNumber.equivalence)

export const PairMetadata: React.FC<PairMetadataProps> = memo(({ pair }) => {
  const [priceType] = useAtom(tradePriceType)
  const model = useMemo(() => reatomPairMetadataModel(pair), [pair])
  const initialize = useAction(model.initialize)
  const [metadata] = useAtom(model)

  const priceUsd = metadata.priceUsd
  const mcap = metadata.marketCapUsd

  const prevPriceUsd = usePreviousDistinct(priceUsd, eqBn)
  const prevMcap = usePreviousDistinct(mcap, eqBn)

  useEffect(() => {
    return initialize()
  }, [initialize])

  const trend = useMemo(() => {
    if (priceType === 'market-cap' && prevMcap) {
      return BigNumber.order(mcap, prevMcap)
    }

    if (priceType === 'absolute' && prevPriceUsd) {
      return BigNumber.order(priceUsd, prevPriceUsd)
    }

    return 0
  }, [priceType, prevMcap, prevPriceUsd, mcap, priceUsd])

  const formattedPrice = useMemo(() => {
    if (priceType === 'market-cap') {
      return formatMarketCapShort(mcap)
    }

    const parts = NumericFormat.formatPresetToParts(
      priceUsd,
      'currency-amount-short',
      {
        currency: '$',
      },
    )
    const formatted = parts.reduce((result, part) => {
      if (part.type === 'compact-decimal-zeroes') {
        return result + (SUBSCRIPT_MAP[part.value] || '')
      }
      return result + part.value
    }, '')

    return formatted
  }, [priceType, priceUsd, mcap])

  const ticker = pair.quote.ticker
  const trendIcon = Ordering.match(trend, {
    onLessThan: () => '↓',
    onEqual: () => '',
    onGreaterThan: () => '↑',
  })

  const title = `${ticker} ${trendIcon} ${formattedPrice} | kol.trade`

  return (
    <>
      <title>{title}</title>
    </>
  )
})

const SUBSCRIPT_MAP: Record<string, string> = {
  [1]: '₁',
  [2]: '₂',
  [3]: '₃',
  [4]: '₄',
  [5]: '₅',
  [6]: '₆',
  [7]: '₇',
  [8]: '₈',
  [9]: '₉',
}
